import type { ModelClientService } from '@app/api/ClientServices';
import type { ModelSubscriptionService } from '@app/api/Subscriptions';

import type { SubscriptionServiceRow, SubscriptionTotals } from '../types';

const roundMoney = (value: number) => Math.round(value * 100) / 100;

/** Стоимость всего количества услуги без скидки */
export const getRowCost = ({ Price, Count }: SubscriptionServiceRow) =>
  roundMoney(Price * Count);

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

const getPercentOfCost = (amount: number, cost: number) =>
  cost > 0 ? (amount / cost) * 100 : 0;

// Как в окне записи: при смене количества сохраняется скидка в рублях,
// а процент пересчитывается. Скидка не может превышать стоимость услуги.
export const withCount = (
  row: SubscriptionServiceRow,
  count: number
): SubscriptionServiceRow => {
  const Count = Math.max(count, 1);
  const cost = getRowCost({ ...row, Count });
  const DiscountAmount = Math.min(row.DiscountAmount, cost);

  return {
    ...row,
    Count,
    DiscountAmount,
    DiscountPercent: getPercentOfCost(DiscountAmount, cost),
  };
};

export const withDiscountAmount = (
  row: SubscriptionServiceRow,
  amount: number
): SubscriptionServiceRow => {
  const cost = getRowCost(row);
  const DiscountAmount = roundMoney(clamp(amount, 0, cost));

  return {
    ...row,
    DiscountAmount,
    DiscountPercent: getPercentOfCost(DiscountAmount, cost),
  };
};

export const withDiscountPercent = (
  row: SubscriptionServiceRow,
  percent: number
): SubscriptionServiceRow => {
  const DiscountPercent = clamp(percent, 0, 100);

  return {
    ...row,
    DiscountPercent,
    DiscountAmount: roundMoney((getRowCost(row) * DiscountPercent) / 100),
  };
};

/** Сумма за всё количество услуги с учётом скидки */
export const getRowTotal = (row: SubscriptionServiceRow) =>
  roundMoney(getRowCost(row) - row.DiscountAmount);

/** Цена одной единицы услуги в абонементе */
export const getRowUnitPrice = (row: SubscriptionServiceRow) =>
  row.Count > 0 ? roundMoney(getRowTotal(row) / row.Count) : 0;

export const getSubscriptionTotals = (
  rows: SubscriptionServiceRow[]
): SubscriptionTotals => {
  const PriceListCost = roundMoney(
    rows.reduce((sum, row) => sum + getRowCost(row), 0)
  );
  const SubscriptionCost = roundMoney(
    rows.reduce((sum, row) => sum + getRowTotal(row), 0)
  );
  const Benefit = roundMoney(PriceListCost - SubscriptionCost);
  const BenefitPercent = PriceListCost
    ? Math.round((Benefit / PriceListCost) * 1000) / 10
    : 0;

  return { PriceListCost, SubscriptionCost, Benefit, BenefitPercent };
};

export const toSubscriptionService = (
  row: SubscriptionServiceRow
): ModelSubscriptionService => ({
  Id: row.Id,
  ClientServiceId: row.ClientServiceId,
  Name: row.Name,
  Count: row.Count,
  Cost: getRowCost(row),
  Discount: row.DiscountAmount,
});

// Цена берётся из текущего прайса, как и при создании; если услуги уже нет
// в справочнике, — из сохранённой стоимости. Сохранённая скидка в рублях
// остаётся, процент пересчитывается от цены по прайсу.
export const fromSubscriptionService = (
  service: ModelSubscriptionService,
  clientService: ModelClientService | undefined
): SubscriptionServiceRow => {
  const savedUnitPrice = service.Count > 0 ? service.Cost / service.Count : 0;

  return withDiscountAmount(
    {
      Id: service.Id,
      ClientServiceId: service.ClientServiceId,
      Name: service.Name ?? clientService?.Name ?? '',
      CategoryName: clientService?.Category ?? null,
      Price: clientService?.PriceS ?? roundMoney(savedUnitPrice),
      Count: service.Count,
      DiscountAmount: 0,
      DiscountPercent: 0,
    },
    service.Discount
  );
};
